import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../components/ui/Toast';
import { useAuthStore } from '../../stores/auth.store';
import {
  useTemplate,
  useCreateTemplate,
  useUpdateTemplate,
  useSendTestTemplate,
  useGenerateEmailTemplate,
  useImportCanva,
} from '../../api/templates.api';
import { MergeFieldInserter } from './builder/MergeFieldInserter';
import { COMMON_MERGE_TOKENS } from '../../lib/merge-tokens';

/* ── Custom HTML template editor (Option 1) ──
   A code-first editor for recruiter-authored email HTML. Deliberately NOT the
   block builder — a full HTML document must be stored/sent verbatim, so this
   view edits `body_html` as code and never round-trips it through the block
   renderer. Left: fields + HTML source. Right: live preview iframe.

   The authoritative preview is still "Send test" (server-side render with real
   sample data); the iframe here does a light client-side token substitution so
   the author can see roughly what recipients get. */

const SAVE_DEBOUNCE_MS = 1500;

// Light preview-only sample values. Unknown tokens are left visible (as
// `{{token}}`) so the author can see where personalisation lands. The real
// send resolves these server-side (missing → empty string).
const PREVIEW_SAMPLE: Record<string, string> = {
  first_name: 'Jordan',
  last_name: 'Lee',
  full_name: 'Jordan Lee',
  name: 'Jordan Lee',
  company: 'Acme Recruiting',
  company_name: 'Acme Recruiting',
  title: 'Senior Engineer',
  email: 'jordan.lee@example.com',
  recruiter_name: 'Alex Morgan',
  unsubscribe_url: '#unsubscribe',
  calendar_url: '#book',
};

function substitutePreview(html: string): string {
  return html.replace(/\{\{\s*([a-zA-Z_][\w.]*)\s*\}\}/g, (m, key: string) =>
    key in PREVIEW_SAMPLE ? PREVIEW_SAMPLE[key] : m,
  );
}

export function CustomHtmlTemplatePage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const { addToast } = useToast();
  const me = useAuthStore((s) => s.user);

  const { data: existing, isLoading } = useTemplate(id);
  const createMut = useCreateTemplate();
  const updateMut = useUpdateTemplate(id ?? '');

  const [name, setName] = useState('New HTML email');
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [html, setHtml] = useState(STARTER_HTML);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'idle' | 'error'>('idle');
  const [sendTestOpen, setSendTestOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setSubject(existing.subject);
      setPreheader(existing.preheader ?? '');
      setHtml(existing.body_html || '');
    }
  }, [existing]);

  // Auto-save after the user has loaded an existing template.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (isNew || !existing) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaveStatus('idle');
    saveTimer.current = setTimeout(() => {
      setSaveStatus('saving');
      updateMut.mutate(
        { name, subject, preheader: preheader || null, kind: 'CUSTOM_HTML', body_html: html },
        { onSuccess: () => setSaveStatus('saved'), onError: () => setSaveStatus('error') },
      );
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, subject, preheader, html]);

  const previewHtml = useMemo(() => substitutePreview(html), [html]);

  const handleSave = async () => {
    setSaveStatus('saving');
    try {
      if (isNew) {
        const created = await createMut.mutateAsync({
          name,
          subject,
          preheader: preheader || null,
          kind: 'CUSTOM_HTML',
          body_html: html,
        });
        setSaveStatus('saved');
        navigate(`/templates/html/${created.id}/edit`, { replace: true });
      } else {
        await updateMut.mutateAsync({
          name,
          subject,
          preheader: preheader || null,
          kind: 'CUSTOM_HTML',
          body_html: html,
        });
        setSaveStatus('saved');
        addToast({ variant: 'success', title: 'Saved' });
      }
    } catch {
      setSaveStatus('error');
      addToast({ variant: 'danger', title: 'Save failed' });
    }
  };

  if (!isNew && isLoading) {
    return <div style={{ padding: 32 }}>Loading…</div>;
  }

  // Guard: block-based templates belong in the visual builder, not here.
  if (existing && existing.blocks) {
    return (
      <div style={{ padding: 32 }}>
        <p style={{ color: 'var(--color-text-muted)' }}>
          This is a block template. Open it in the visual builder instead.
        </p>
        <button type="button" onClick={() => navigate(`/templates/${existing.id}/edit`)} style={btnPrimary()}>
          Open in builder
        </button>
      </div>
    );
  }
  if (existing?.is_system) {
    return (
      <div style={{ padding: 32 }}>
        <p style={{ color: 'var(--color-danger, #e03131)' }}>
          System templates are read-only. Duplicate it from the gallery to make an editable copy.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 16px',
          background: '#fff',
          border: '1px solid var(--color-border-default, #ececf2)',
          borderRadius: 8,
        }}
      >
        <button
          type="button"
          onClick={() => navigate('/templates')}
          aria-label="Exit"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--color-text-muted)' }}
        >
          ←
        </button>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Template name"
          style={{
            flex: 1,
            padding: '6px 10px',
            border: '1px solid transparent',
            borderRadius: 6,
            fontFamily: 'inherit',
            fontSize: 16,
            fontWeight: 700,
            color: 'var(--color-text-brand, #1e1b4b)',
            background: 'transparent',
          }}
        />
        <SaveStatusBadge status={saveStatus} />
        <button type="button" onClick={() => setImportOpen(true)} data-testid="import-canva-btn" style={btnGhost()}>Import</button>
        <button type="button" onClick={() => setAiOpen(true)} data-testid="ai-generate-btn" style={btnGhost()}>✨ Generate with AI</button>
        <button type="button" onClick={() => setSendTestOpen(true)} style={btnGhost()}>Send test</button>
        <button type="button" onClick={handleSave} data-testid="save-html-btn" style={btnPrimary()}>Save</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12, flex: 1, minHeight: 0 }}>
        {/* Left — fields + HTML source */}
        <section
          data-testid="html-source-pane"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            background: '#fff',
            border: '1px solid var(--color-border-default, #ececf2)',
            borderRadius: 8,
            padding: 14,
            overflow: 'auto',
          }}
        >
          <Field label="Subject">
            <TextWithInserter value={subject} onChange={setSubject} />
          </Field>
          <Field label="Preheader (inbox preview text)">
            <TextWithInserter value={preheader} onChange={setPreheader} />
          </Field>
          <Field label="HTML">
            <HtmlWithInserter value={html} onChange={setHtml} />
          </Field>
          <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: 0 }}>
            Paste email-safe HTML (table layout, inline CSS). Scripts, forms and unsafe URLs are stripped on save.
            Use {'{{first_name}}'} etc. for personalisation, and include an {'{{unsubscribe_url}}'} link.
            Always <strong>Send test</strong> before using it in a campaign.
          </p>
        </section>

        {/* Right — live preview */}
        <section
          data-testid="html-preview-pane"
          style={{
            display: 'flex',
            flexDirection: 'column',
            background: '#fff',
            border: '1px solid var(--color-border-default, #ececf2)',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <header style={{ padding: '10px 14px', borderBottom: '1px solid var(--color-border-default, #ececf2)', fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.04 }}>
            Preview
          </header>
          <iframe
            title="Custom HTML preview"
            srcDoc={previewHtml}
            // Visual preview only — scripts blocked (allow-scripts omitted);
            // same-origin lets nothing here since we don't introspect it.
            sandbox=""
            style={{ flex: 1, width: '100%', border: 0, background: '#fff' }}
          />
        </section>
      </div>

      {sendTestOpen && (
        <SendTestModal
          templateId={id ?? ''}
          defaultEmail={me?.email ?? ''}
          disabled={isNew}
          onClose={() => setSendTestOpen(false)}
        />
      )}

      {aiOpen && (
        <AiGenerateModal
          onClose={() => setAiOpen(false)}
          onApply={(r) => {
            setSubject(r.subject);
            setPreheader(r.preheader);
            setHtml(r.body_html);
            setAiOpen(false);
          }}
        />
      )}

      {importOpen && (
        <ImportModal
          onClose={() => setImportOpen(false)}
          onApply={(r) => {
            if (r.subject) setSubject(r.subject);
            setHtml(r.body_html);
            setImportOpen(false);
          }}
        />
      )}
    </div>
  );
}

/* ── Import from Canva ── */

function ImportModal({
  onClose,
  onApply,
}: {
  onClose: () => void;
  onApply: (r: { subject: string; body_html: string }) => void;
}) {
  const [tab, setTab] = useState<'zip' | 'paste'>('zip');
  const [file, setFile] = useState<File | null>(null);
  const [pastedHtml, setPastedHtml] = useState('');
  const mut = useImportCanva();
  const { addToast } = useToast();

  const canSubmit = tab === 'zip' ? !!file : pastedHtml.trim().length > 0;

  const handleImport = async () => {
    if (!canSubmit) return;
    try {
      const r =
        tab === 'zip' && file
          ? await mut.mutateAsync({ file })
          : await mut.mutateAsync({ html: pastedHtml });
      onApply({ subject: r.subject, body_html: r.body_html });
      const skipped = r.images_skipped > 0 ? `; ${r.images_skipped} could not be resolved` : '';
      addToast({
        variant: 'success',
        title: 'Imported',
        description: `${r.images_rehosted} image(s) copied into Engage${skipped}. Review, then Send test before using it.`,
      });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Import failed';
      addToast({ variant: 'danger', title: 'Import failed', description: msg });
    }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 560, maxWidth: '92vw' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: 'var(--color-text-brand)' }}>Import from Canva</h2>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
          In Canva, export your email as <strong>“HTML and images”</strong> and upload the ZIP, or paste raw HTML. Images are re-hosted on Engage and the HTML is sanitised. Importing replaces the current subject and HTML.
        </p>

        <div role="tablist" style={{ display: 'inline-flex', border: '1px solid var(--color-border-default)', borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
          {(['zip', 'paste'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              style={{
                padding: '6px 14px',
                background: tab === t ? 'var(--color-action-primary, #1e1b4b)' : '#fff',
                color: tab === t ? '#fff' : 'var(--color-text-muted)',
                border: 'none',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t === 'zip' ? 'Upload ZIP' : 'Paste HTML'}
            </button>
          ))}
        </div>

        {tab === 'zip' ? (
          <input
            type="file"
            accept=".zip,application/zip,application/x-zip-compressed"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            style={{ display: 'block', fontSize: 13, marginBottom: 8 }}
          />
        ) : (
          <textarea
            value={pastedHtml}
            onChange={(e) => setPastedHtml(e.target.value)}
            rows={7}
            spellCheck={false}
            placeholder="Paste the email HTML…"
            style={{ ...inputStyle, minHeight: 150, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 12, whiteSpace: 'pre' }}
          />
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" onClick={onClose} style={btnGhost()}>Cancel</button>
          <button type="button" onClick={handleImport} disabled={mut.isPending || !canSubmit} style={btnPrimary()}>
            {mut.isPending ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── AI generate ── */

function AiGenerateModal({
  onClose,
  onApply,
}: {
  onClose: () => void;
  onApply: (r: { subject: string; preheader: string; body_html: string }) => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState('professional');
  const mut = useGenerateEmailTemplate();
  const { addToast } = useToast();

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    try {
      const r = await mut.mutateAsync({ prompt, tone });
      onApply({ subject: r.subject, preheader: r.preheader, body_html: r.body_html });
      addToast({
        variant: 'success',
        title: 'Draft generated',
        description: 'Review it, then Send test before using it in a campaign.',
      });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Generation failed';
      addToast({ variant: 'danger', title: 'Generation failed', description: msg });
    }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 520, maxWidth: '92vw' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, color: 'var(--color-text-brand)' }}>Generate with AI</h2>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
          Describe the email: we'll produce email-safe HTML using your brand kit and merge tokens. Generating replaces the current subject, preheader and HTML.
        </p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)' }}>Brief</span>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={5}
            placeholder="e.g. A warm re-engagement email to past candidates about a new senior backend role in Sydney, with a button to book a call."
            style={{ ...inputStyle, minHeight: 110, resize: 'vertical' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)' }}>Tone</span>
          <select value={tone} onChange={(e) => setTone(e.target.value)} style={inputStyle}>
            {['professional', 'friendly', 'direct', 'warm'].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" onClick={onClose} style={btnGhost()}>Cancel</button>
          <button type="button" onClick={handleGenerate} disabled={mut.isPending || !prompt.trim()} style={btnPrimary()}>
            {mut.isPending ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Fields ── */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)' }}>{label}</span>
      {children}
    </label>
  );
}

function TextWithInserter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [caret, setCaret] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? value.length)}
        style={inputStyle}
      />
      <MergeFieldInserter tokens={COMMON_MERGE_TOKENS} onInsert={onChange} value={value} caret={caret} />
    </div>
  );
}

function HtmlWithInserter({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [caret, setCaret] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onSelect={(e) => setCaret(e.currentTarget.selectionStart ?? value.length)}
        spellCheck={false}
        style={{
          ...inputStyle,
          flex: 1,
          minHeight: 360,
          resize: 'vertical',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: 'pre',
          overflowWrap: 'normal',
        }}
      />
      <MergeFieldInserter tokens={COMMON_MERGE_TOKENS} onInsert={onChange} value={value} caret={caret} />
    </div>
  );
}

/* ── Send test ── */

function SendTestModal({
  templateId,
  defaultEmail,
  disabled,
  onClose,
}: {
  templateId: string;
  defaultEmail: string;
  disabled: boolean;
  onClose: () => void;
}) {
  const [to, setTo] = useState(defaultEmail);
  const mut = useSendTestTemplate(templateId);
  const { addToast } = useToast();

  const handleSend = async () => {
    try {
      await mut.mutateAsync({ to });
      addToast({ variant: 'success', title: `Test sent to ${to}` });
      onClose();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Send failed';
      addToast({ variant: 'danger', title: 'Test send failed', description: msg });
    }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90vw' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: 'var(--color-text-brand)' }}>Send test</h2>
        {disabled && (
          <p style={{ color: 'var(--color-warning, #d97706)', fontSize: 12, marginBottom: 8 }}>
            Save the template first. Test sends need a saved template.
          </p>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)' }}>To</span>
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle} disabled={disabled} />
        </label>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button type="button" onClick={onClose} style={btnGhost()}>Cancel</button>
          <button type="button" onClick={handleSend} disabled={disabled || mut.isPending} style={btnPrimary()}>
            {mut.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SaveStatusBadge({ status }: { status: 'saved' | 'saving' | 'idle' | 'error' }) {
  const map = {
    saved: { text: 'Saved', color: 'var(--color-success, #2f9e44)' },
    saving: { text: 'Saving…', color: 'var(--color-text-muted, #6b6b7d)' },
    idle: { text: '', color: 'var(--color-text-muted, #6b6b7d)' },
    error: { text: 'Save failed', color: 'var(--color-danger, #e03131)' },
  } as const;
  return <span style={{ fontSize: 12, color: map[status].color, minWidth: 60 }}>{map[status].text}</span>;
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--color-border-default, #ececf2)',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
  flex: 1,
  width: '100%',
  boxSizing: 'border-box',
};

function btnPrimary(): React.CSSProperties {
  return {
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 600,
    background: 'var(--color-action-primary, #1e1b4b)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
}
function btnGhost(): React.CSSProperties {
  return {
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 600,
    background: '#fff',
    color: 'var(--color-text-secondary, #2b2b3c)',
    border: '1px solid var(--color-border-default, #ececf2)',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
}

const STARTER_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
</head>
<body style="margin:0; padding:0; background:#f4f5f9;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f4f5f9;">
    <tr>
      <td align="center" style="padding:24px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px; background:#ffffff; border-radius:8px;">
          <tr>
            <td style="padding:32px; font-family:Arial,sans-serif; color:#2b2b3c; font-size:16px; line-height:1.6;">
              <p>Hi {{first_name}},</p>
              <p>Write your email here.</p>
              <p style="font-size:12px; color:#888;">
                <a href="{{unsubscribe_url}}">Unsubscribe</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
